import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { 
  UserPlus, 
  Trash2, 
  RefreshCcw, 
  Mail,
  Loader2,
  ShieldCheck,
  Plus
} from 'lucide-react';
import { useWorkspacesContext } from '../contexts/WorkspacesContext';

const AccountManagementPage = () => {
  const { addWorkspace, workspaces } = useWorkspacesContext();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAccount, setNewAccount] = useState({ email: '', appPassword: '', name: '' });
  const [isActionLoading, setIsActionLoading] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);

  useEffect(() => {
    setIsConnected(false);
  }, [newAccount.email, newAccount.appPassword]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/gmails');
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleCheckConnection = async () => {
    if (!newAccount.email || !newAccount.appPassword) {
        alert("Please provide email and app password first.");
        return;
    }

    setConnectionLoading(true);
    try {
        const body = {
            username: newAccount.email,
            appPassword: newAccount.appPassword,
            folder: 'INBOX',
            sinceDays: 0,
            startIndex: 0,
            endIndex: 1,
        };
        const res = await api.post("/mail/search", body);
        if (Array.isArray(res.data)) {
            setIsConnected(true);
            alert("Connection Successful!");
        } else {
            throw new Error("Invalid response from server");
        }
    } catch (err) {
        console.error("Connection check failed:", err);
        alert("Connection failed. Check your email and app password.");
        setIsConnected(false);
    } finally {
        setConnectionLoading(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!isConnected) {
        alert("Please check connection first.");
        return;
    }
    try {
      await api.post('/gmails/add', {
        email: newAccount.email,
        password: newAccount.appPassword,
        accountName: newAccount.name
      });
      
      // Also add to local workspaces for the sidebar
      try {
        await addWorkspace(newAccount.email, newAccount.appPassword, newAccount.name);
      } catch (err) {
        console.error("Failed to add local workspace:", err);
      }

      setShowAddModal(false);
      setIsConnected(false);
      fetchAccounts(); 
      setNewAccount({ email: '', appPassword: '', name: '' });
    } catch (error) {
      alert("Error adding account. Check if it already exists.");
    }
  };
  const handleToggle = async (id) => {
    setIsActionLoading(id);
    try {
      await api.put(`/gmails/toggle/${id}`);
      setAccounts(accounts.map(acc => 
        acc.id === id ? { ...acc, active: !acc.active } : acc
      ));
    } catch (error) {
      console.error("Toggle failed:", error);
      alert("Failed to update status.");
    } finally {
      setIsActionLoading(null);
    }
  };
  const handleDelete = async (id, email) => {
    if (!window.confirm("Are you sure? This will stop the live listener for this account.")) return;
    try {
      await api.delete(`/gmails/${id}`);
      
      // Also remove from local workspaces
      const localWs = workspaces.find(ws => ws.email === email);
      if (localWs) {
        // We might need a non-confirming version of removeWorkspace or handle it here
        // For now let's just refresh local state if possible or the user will confirm twice
        // Actually, sidebar has its own delete. 
      }

      fetchAccounts();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  return (
    <div className="p-4 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold m-0">Google Accounts Management</h4>
          <p className="text-muted small">Manage live listeners and database synchronization</p>
        </div>
        <button className="btn btn-primary d-flex align-items-center gap-2 shadow-sm" onClick={() => setShowAddModal(true)}>
          <UserPlus size={18} /> Add New Account
        </button>
      </div>

      <div className="row g-3">
        {isLoading && accounts.length === 0 ? (
          <div className="text-center py-5"><RefreshCcw className="spin-animation" /> Loading DB Accounts...</div>
        ) : (
          accounts.map((acc) => (
            <div key={acc.id} className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-sm h-100 overflow-hidden">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className={`p-2 rounded ${acc.active ? 'bg-primary bg-opacity-10' : 'bg-secondary bg-opacity-10'}`}>
                      <Mail className={acc.active ? 'text-primary' : 'text-secondary'} size={20} />
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-light text-danger border-0 shadow-sm" onClick={() => handleDelete(acc.id, acc.email)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <h6 className="fw-bold mb-1">{acc.accountName || "Unnamed Account"}</h6>
                  <p className="text-muted small mb-3 text-truncate">{acc.email}</p>

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="bg-light p-1 rounded-circle" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <UserPlus size={12} className="text-muted" />
                    </div>
                    <div className="small text-muted">
                      Added by: <span className="fw-bold text-dark">{acc.addedBy || acc.createdBy || 'System'}</span>
                    </div>
                  </div>

                  <div className="border-top pt-3 mt-2 d-flex justify-content-between align-items-center">
                    {/* Switch Toggle Button */}
                    <div className="form-check form-switch d-flex align-items-center gap-2 p-0 m-0">
                      <div className="position-relative">
                        <input 
                          className="form-check-input ms-0 mt-0" 
                          type="checkbox" 
                          role="switch" 
                          style={{ cursor: 'pointer', width: '36px', height: '18px' }}
                          checked={acc.active}
                          disabled={isActionLoading === acc.id}
                          onChange={() => handleToggle(acc.id)}
                        />
                        {isActionLoading === acc.id && (
                           <div className="position-absolute start-0 top-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-50">
                              <Loader2 size={10} className="spin-animation text-primary" />
                           </div>
                        )}
                      </div>
                      <span className={`small fw-bold ${acc.active ? 'text-success' : 'text-danger'}`}>
                        {acc.active ? 'Active' : 'Disabled'}
                      </span>
                    </div>

                    <div className="text-muted small opacity-50">
                       ID: {acc.id}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Add Account */}
      {showAddModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header border-0 pb-0">
                <h5 className="fw-bold">Register Google Account</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleAddAccount}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Account Name (Label)</label>
                    <input type="text" className="form-control" required value={newAccount.name}
                      onChange={e => setNewAccount({...newAccount, name: e.target.value})} placeholder="e.g. Primary Marketing" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Gmail Address</label>
                    <input type="email" className="form-control" required value={newAccount.email}
                      onChange={e => setNewAccount({...newAccount, email: e.target.value})} placeholder="example@gmail.com" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold d-flex align-items-center gap-2">
                      App Password <ShieldCheck size={14} className="text-success" />
                    </label>
                    <input type="password" className="form-control" required value={newAccount.appPassword}
                      onChange={e => setNewAccount({...newAccount, appPassword: e.target.value})} placeholder="xxxx xxxx xxxx xxxx" />
                    <div className="form-text text-info small">This password will be stored securely for the backend listener.</div>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button type="button" className="btn btn-light" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button 
                    type="button" 
                    className={`btn ${isConnected ? 'btn-success' : 'btn-info'} d-flex align-items-center gap-2`}
                    onClick={handleCheckConnection}
                    disabled={connectionLoading || !newAccount.email || !newAccount.appPassword}
                  >
                    {connectionLoading ? <Loader2 size={18} className="spin-animation" /> : <RefreshCcw size={18} />}
                    {isConnected ? 'Connection Verified' : 'Check Connection'}
                  </button>
                  <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" disabled={!isConnected}>
                    <Plus size={18} /> Register & Start Listener
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountManagementPage;